import { createContext, useCallback, useContext, useEffect, useState, type PropsWithChildren } from 'react';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface AuthTenant {
  id: string;
  slug: string;
  businessName: string;
  status: string;
}

interface AuthState {
  user: AuthUser | null;
  tenant: AuthTenant | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ tenantSlug: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState>({
  user: null,
  tenant: null,
  isLoading: true,
  login: () => Promise.reject(new Error('not ready')),
  logout: () => Promise.resolve(),
  refresh: () => Promise.resolve(),
});

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tenant, setTenant] = useState<AuthTenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (!res.ok) {
        setUser(null);
        setTenant(null);
        return;
      }
      const body = (await res.json()) as { user: AuthUser; tenant: AuthTenant };
      setUser(body.user);
      setTenant(body.tenant);
    } catch {
      setUser(null);
      setTenant(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? 'Login failed');
    }
    const data = (await res.json()) as { user: AuthUser; tenantSlug: string };
    await refresh();
    return { tenantSlug: data.tenantSlug };
  }, [refresh]);

  const logout = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setUser(null);
    setTenant(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, tenant, isLoading, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthCtx);
}
